// La portada de Videos cortos en dos pasos (ADR-2026-10-30-videos-cortos-con-el-agente), como la
// del Catálogo:
//  1. Elegir los productos (`ElegirProductosDeSesion`, hasta 50), con "Mis tandas" a la derecha.
//  2. La intro del agente (`IntroDeVideosCortos`): Adelante, o aclaraciones. Mandar crea la
//     sesión (un chat de tipo `videos-cortos`), guarda el plan vacío con los productos, carga ese
//     chat y abre la sesión, que le manda el pedido al agente.
import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useChatAtlas } from '../../../../chat-atlas/ChatAtlasContext';
import { useSidebar } from '../../../../context/SidebarContext';
import type { ChatAttachment } from '../../agentAttachments';
import { createChat, listSesionesVideosCortos, TIPO_CHAT_VIDEOS_CORTOS, type SesionDeCatalogoResumen } from '../../agentChatApi';
import { useAgentEnv } from '../../agentEnvContext';
import type { MockProductV2 } from '../../mockData';
import { ElegirProductosDeSesion, ListaDeSesiones } from '../shared/ElegirProductosDeSesion';
import { IntroDeVideosCortos } from './IntroDeVideosCortos';
import { MAX_PRODUCTOS, planVacio, type PlanDeVideos } from './plan';
import { guardarPlanDeVideos } from './planGuardado';
import { RUTA_VIDEOS_CORTOS, rutaDeSesionDeVideos } from './rutas';

/** Lo que la portada le deja a la sesión: el pedido con sus adjuntos y el plan recién guardado
 *  (así la sesión se dibuja en el primer frame, sin esperar a leerlo). */
export interface EstadoDeLaPortadaDeVideos {
  primerMensaje: { texto: string; adjuntos: ChatAttachment[] };
  plan: PlanDeVideos;
}

const pideMenosMovimiento = (): boolean => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

export const PortadaDeVideosCortos: React.FC<{ rubro: string | null; onProductosNuevos?: () => void }> = ({ rubro, onProductosNuevos }) => {
  const navigate = useNavigate();
  const { userId, companyId, brandId } = useAgentEnv();
  const { cargarChat } = useChatAtlas();
  const [paso, setPaso] = useState<'productos' | 'intro'>('productos');
  // En el teléfono el botón de atrás del shell retrocede DE PASO, como en el Catálogo.
  const { setMobileBackOverride } = useSidebar();
  useEffect(() => {
    setMobileBackOverride(() => () => {
      if (paso === 'intro') setPaso('productos');
      else navigate('/interfaz-v2');
    });
    return () => setMobileBackOverride(null);
  }, [paso, setMobileBackOverride, navigate]);
  const [elegidos, setElegidos] = useState<MockProductV2[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sesiones, setSesiones] = useState<SesionDeCatalogoResumen[]>([]);
  const vivo = useRef(true);
  useEffect(() => {
    vivo.current = true;
    void listSesionesVideosCortos().then((s) => {
      if (vivo.current) setSesiones(s);
    });
    return () => {
      vivo.current = false;
    };
  }, []);

  const motivoBloqueo = elegidos.length === 0 ? 'Elegí al menos un producto.' : elegidos.length > MAX_PRODUCTOS ? `Hasta ${MAX_PRODUCTOS} productos por lote (elegiste ${elegidos.length}).` : null;

  const enviar = useCallback(
    async (texto: string, adjuntos: ChatAttachment[]) => {
      if (!userId || !companyId || motivoBloqueo || enviando) return;
      setEnviando(true);
      setError(null);
      try {
        const plan = planVacio(
          elegidos.map((p) => ({ varianteId: p.id, nombre: p.name, ...(p.imageUrl ? { portadaUrl: p.imageUrl } : {}) })),
          texto,
        );
        const id = await createChat({ userId, companyId, title: plan.titulo, messages: [], tipo: TIPO_CHAT_VIDEOS_CORTOS });
        if (!id) throw new Error('No se pudo crear la sesión.');
        await guardarPlanDeVideos(id, plan);
        // El chat se carga ANTES de abrir la sesión: si no, la conversación mostraría un instante
        // la que estaba abierta antes.
        await cargarChat(id);
        if (!vivo.current) return;
        const estado: EstadoDeLaPortadaDeVideos = { primerMensaje: { texto, adjuntos }, plan };
        const abrir = () => navigate(rutaDeSesionDeVideos(id), { state: estado });
        const doc = document as Document & { startViewTransition?: (cb: () => void) => unknown };
        if (doc.startViewTransition && !pideMenosMovimiento()) doc.startViewTransition(() => flushSync(abrir));
        else abrir();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'No se pudo abrir la sesión. Probá de nuevo.');
        setEnviando(false);
      }
    },
    [userId, companyId, motivoBloqueo, enviando, elegidos, cargarChat, navigate],
  );

  if (paso === 'intro') {
    return <IntroDeVideosCortos productos={elegidos} enviando={enviando} error={error} onEnviar={(t, a) => void enviar(t, a)} onVolver={() => setPaso('productos')} />;
  }

  return (
    <ElegirProductosDeSesion
      titulo="Elegí los productos para tus videos"
      migas={[{ label: 'Inicio', onClick: () => navigate('/interfaz-v2') }, { label: 'Videos cortos', onClick: () => navigate(RUTA_VIDEOS_CORTOS) }]}
      maxProductos={MAX_PRODUCTOS}
      unidad="lote"
      derecha={<ListaDeSesiones etiqueta="Mis lotes" sesiones={sesiones} onAbrir={(id) => navigate(rutaDeSesionDeVideos(id))} />}
      rubro={rubro}
      companyId={companyId}
      brandId={brandId ?? null}
      onProductosNuevos={onProductosNuevos}
      elegidos={elegidos}
      onElegidos={setElegidos}
      motivoBloqueo={motivoBloqueo}
      onSiguiente={() => setPaso('intro')}
      onSalir={() => navigate('/interfaz-v2')}
    />
  );
};
